import type { ReactNode } from 'react';
import { forwardRef, useState } from 'react';

import { Box, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';

import Control from '@shared/ui-kit/selects/MuiSelect/Control';
import { MenuList } from '@shared/ui-kit/selects/MuiSelect/MenuList';
import Placeholder from '@shared/ui-kit/selects/MuiSelect/Placeholder';
import SwitcherMenuList from '@shared/ui-kit/selects/MuiSelect/SwitcherMenuList/SwitcherMenuList';
import { InputError, InputLabel } from 'shared/ui-kit';

import { ClearIndicator } from './ClearIndicator';
import { DefaultNoOptionsMessage } from './DefaultNoOptionsMessage/DefaultNoOptionsMessage';
import { DropdownIndicator } from './DropdownIndicator';
import { setSelectStyle } from './MuiSelect.style';
import { NoOptionsMessage } from './NoOptionsMessage/NoOptionsMessage';
import { Option } from './Option';
import { SelectContextProvider } from './SelectContext';
import SingleValue from './SingleValue';

import type { TabType } from '@shared/ui-kit/tabs/NavigationTabs/types';
import type { ActionMeta } from 'react-select';
import type { OptionType } from 'shared/ui-kit';

type MuiSelectProps = {
  id?: string;
  selectId?: string;
  labelId?: string;
  label?: string;
  options: OptionType[] | any;
  value?: OptionType[] | any;
  size?: 'small' | 'medium';
  placeholder?: string;
  inputVariant?: 'outline' | 'text';
  isError?: boolean;
  helpText?: string;
  isRequired?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  isDisabled?: boolean;
  getAsyncData?: () => void;
  clearAsyncData?: () => void;
  isLoading?: boolean;
  isMulti?: boolean;
  minWidthSelectMenu?: number;
  maxHeightSelectMenu?: number;
  isCreatable?: boolean;
  isShowAvatarInOptions?: boolean;
  startIcon?: ReactNode;
  onChange: (option: any, actionMeta: ActionMeta<OptionType>) => void;
  controlShouldRenderValue?: boolean;
  isDisableKeyboardClear?: boolean;
  isNoOptionsMessageCreateContact?: boolean;
  isNoOptionsMessageCreateOnlyCompanyContact?: boolean;
  selectTopContainer?: ReactNode;
  classNameForTargetUserOnboardingTooltip?: string;
  minHeightSelectMenu?: number;
  isSwitcherMenuList?: boolean;
  isTransparentDefaultBackground?: boolean;
  switcherFirstBtnClick?: () => void;
  switcherSecondBtnClick?: () => void;
  switcherTabs?: TabType[];
};
// TODO change global props invite
// TODO Change loader component
// TODO onChange any type
// TODO Style clear and open indicator

export const MuiSelect = forwardRef<any, MuiSelectProps>(
  (
    {
      id,
      selectId,
      labelId,
      label,
      placeholder = '',
      isError = false,
      maxHeightSelectMenu,
      inputVariant = 'outline',
      helpText = '',
      options = [],
      value,
      isRequired,
      isClearable = false,
      isCreatable = false,
      isSearchable = false,
      isDisabled = false,
      isLoading = false,
      minWidthSelectMenu,
      isMulti = false,
      isTransparentDefaultBackground = true,
      isShowAvatarInOptions = false,
      startIcon,
      size = 'medium',
      onChange,
      getAsyncData,
      clearAsyncData,
      controlShouldRenderValue = true,
      isDisableKeyboardClear = false,
      isNoOptionsMessageCreateContact = true,
      isNoOptionsMessageCreateOnlyCompanyContact = false,
      selectTopContainer,
      classNameForTargetUserOnboardingTooltip,
      minHeightSelectMenu,
      isSwitcherMenuList = false,
      switcherFirstBtnClick,
      switcherSecondBtnClick,
      switcherTabs,
      ...arg
    },
    ref,
  ) => {
    const theme = useTheme();
    const isSmallDisplay = useMediaQuery(`${theme.breakpoints.down('md')}`);
    const [searchValue, setSearchValue] = useState('');
    const [tabsIndex, setTabsIndex] = useState<number>(0);

    const onKeyDown = (e: any) => {
      if (!isDisableKeyboardClear) return;
      if (e.keyCode === 8 && searchValue.length === 0) {
        e.preventDefault();
      }
    };

    const handleInputChange = (inputValue: string) => {
      setSearchValue(inputValue);
    };

    const onMnuOpen = () => {
      setTimeout(() => {
        const selectedEl = document.querySelector('.mydropdown__option--is-selected');
        const dropdownMenuList = document.querySelector('.mydropdown__menu-list');

        if (selectedEl && dropdownMenuList) {
          const selectedRect = selectedEl.getBoundingClientRect();
          const dropdownRect = dropdownMenuList.getBoundingClientRect();
          const offset = selectedRect.top - dropdownRect.top + dropdownMenuList.scrollTop;
          const newTop = offset - (dropdownMenuList.clientHeight / 2 - selectedEl.clientHeight / 2);
          dropdownMenuList.scrollTo({
            top: newTop,
            behavior: 'smooth',
          });
        }
      }, 15);
    };

    const switcherMenuListStyles = isSwitcherMenuList && {
      borderRadius: '8px',
      border: 'none',
      padding: 0,
    };

    const selectStyle = setSelectStyle(
      isError,
      size,
      !!startIcon,
      isClearable,
      inputVariant,
      minWidthSelectMenu,
      maxHeightSelectMenu,
      minHeightSelectMenu,
      switcherMenuListStyles,
      isTransparentDefaultBackground,
    );
    return (
      <Box
        id={id}
        sx={{
          position: 'relative',
          width: inputVariant === 'text' ? 'max-content' : 'auto',
          maxWidth: '100%',
        }}
      >
        {label ? <InputLabel label={label} isRequired={isRequired} id={labelId} /> : <></>}
        <SelectContextProvider value={{ isShowAvatarInOptions }}>
          {isCreatable ? (
            <CreatableSelect
              {...arg}
              id={selectId}
              instanceId="custom"
              ref={ref}
              inputId="inputId"
              onMenuOpen={onMnuOpen}
              className={'mydropdown'}
              classNamePrefix={'mydropdown'}
              placeholder={placeholder}
              value={value}
              defaultValue={value}
              options={options}
              isSearchable={isSearchable}
              isDisabled={isDisabled}
              components={{
                DropdownIndicator: (props) => (
                  <DropdownIndicator
                    classNameForTargetUserOnboardingTooltip={classNameForTargetUserOnboardingTooltip}
                    {...props}
                  />
                ),
                ClearIndicator,
                Option,
              }}
              onKeyDown={onKeyDown}
              onChange={(newValue, actionMeta) => {
                // eslint-disable-next-line no-underscore-dangle
                if (newValue?.__isNew__) {
                  // eslint-disable-next-line no-underscore-dangle
                  return onChange({ ...newValue, isNew: newValue.__isNew__ }, actionMeta);
                }
                return onChange(newValue, actionMeta);
              }}
              escapeClearsValue={false}
              isMulti={isMulti}
              styles={selectStyle}
              isClearable={isClearable}
              controlShouldRenderValue={controlShouldRenderValue}
              isLoading={isLoading}
              onFocus={() => {
                getAsyncData?.();
              }}
              onBlur={() => {
                clearAsyncData?.();
              }}
            />
          ) : (
            <Select
              {...arg}
              id={selectId}
              instanceId="custom"
              ref={ref}
              inputId="inputId"
              defaultValue={value}
              placeholder={placeholder}
              value={value}
              options={options}
              onMenuOpen={onMnuOpen}
              className={'mydropdown'}
              classNamePrefix={'mydropdown'}
              // @ts-ignore
              startIcon={startIcon}
              isOptionDisabled={(option) => (option.isDisabled ? option.isDisabled : false)}
              isSearchable={isSmallDisplay && !isMulti ? false : isSearchable}
              isDisabled={isDisabled}
              onChange={onChange}
              onInputChange={handleInputChange}
              onKeyDown={onKeyDown}
              escapeClearsValue={false}
              isMulti={isMulti}
              styles={selectStyle}
              components={{
                DropdownIndicator: (props) => (
                  <DropdownIndicator
                    {...props}
                    classNameForTargetUserOnboardingTooltip={classNameForTargetUserOnboardingTooltip}
                  />
                ),
                ClearIndicator,
                SingleValue: (props) => {
                  return <SingleValue {...props} />;
                },
                Placeholder: (props) => {
                  return <Placeholder {...props} />;
                },
                Control,
                Option,
                MenuList: (props) => {
                  return isSwitcherMenuList ? (
                    <SwitcherMenuList
                      {...props}
                      minHeightSelectMenu={minHeightSelectMenu}
                      switcherFirstBtnClick={switcherFirstBtnClick}
                      switcherSecondBtnClick={switcherSecondBtnClick}
                      tabs={switcherTabs}
                      tabsIndex={tabsIndex}
                      setTabsIndex={setTabsIndex}
                    />
                  ) : (
                    <MenuList {...props} selectTopContainer={selectTopContainer} />
                  );
                },
                NoOptionsMessage: (event) => {
                  return isShowAvatarInOptions ? (
                    <NoOptionsMessage
                      onChangeAssignPeopleListValue={onChange}
                      assignPeopleListValue={[...event.getValue()]}
                      isCreateContact={isNoOptionsMessageCreateContact}
                      isCreateOnlyCompanyContact={isNoOptionsMessageCreateOnlyCompanyContact}
                    />
                  ) : (
                    <DefaultNoOptionsMessage />
                  );
                },
              }}
              controlShouldRenderValue={controlShouldRenderValue}
              isClearable={isClearable}
              isLoading={isLoading}
              onFocus={() => {
                getAsyncData?.();
              }}
              onBlur={() => {
                clearAsyncData?.();
              }}
            />
          )}
        </SelectContextProvider>

        <InputError message={helpText} isShow={isError} />
      </Box>
    );
  },
);
